import moment from "moment-timezone";
import type { Meeting } from "@platform/types/platform.types";

export type Args = {
  meetings: Meeting[];
  options?: {
    anchorDate?: string;
  };
};

/**
 * Returns the next meeting whose joinable window is still open at the anchor:
 * - the earliest scheduled meeting whose end_time is in the future, OR
 * - the first ad-hoc meeting (no end_time) when no future scheduled meeting matches.
 *
 * Used by the dashboard banner as a fallback when getClosestMeetingToCurrentTime
 * returns a meeting that has already ended (the 24h-grace branch). Without this
 * fallback, the join button hides for the ~24h after each scheduled meeting ends.
 */
export function getNextUpcomingMeeting({
  meetings,
  options = {},
}: Args): Meeting | null {
  if (!meetings || meetings.length === 0) return null;

  const anchor = options.anchorDate
    ? moment.tz(options.anchorDate, "UTC")
    : moment.utc();

  const futureScheduled = meetings
    .filter((m) => m.start_time && m.end_time)
    .filter((m) => moment(m.end_time as string).isAfter(anchor))
    .sort(
      (a, b) =>
        moment(a.start_time as string).valueOf() -
        moment(b.start_time as string).valueOf()
    );

  if (futureScheduled.length > 0) return futureScheduled[0];

  const adHoc = meetings.find((m) => !m.start_time || !m.end_time);
  return adHoc ?? null;
}

export default getNextUpcomingMeeting;
